import type {
	AnchorGeneratedFamilyQueryModel,
	AnchorGeneratedFamilyQueryResponseModel,
	AnchorResponseModel,
	AnchorsAlertsInfoModel,
	AnchorsAlertsInfoQueryModel,
	AnchorsAlertsItemModel,
	AnchorsQueryModel,
	AnchorsQueryResponseModel,
	AnchorsSalesEntityResponseModel,
	AnchorStrategyGroupMatchedModel,
	AnchorStrategyMatchedProductsRequest,
	AnchorStrategyQueryModel,
	AnchorStrategyQueryResponseModel,
	FilterRuleModel,
	HTTPValidationError,
	ProductCatalogQueryModel,
	ProductCatalogQueryResponseModel,
	RequestParams,
} from '@/shared/generated'
import type { UseQueryOptions } from '@/shared/lib/query-client'
import type { AxiosError } from 'axios'

import { useQuery } from '@tanstack/react-query'
import { head, isEmpty } from 'lodash/fp'

import { client } from '@/shared/api/client'
import { Operators } from '@/shared/components/query-builder'

import { ANCHORS_TYPES } from '../lib/constants'

export function useAnchorsQuery<T = AnchorsQueryResponseModel>(
	params: AnchorsQueryModel,
	options?: UseQueryOptions<
		AnchorsQueryResponseModel,
		AxiosError<HTTPValidationError>,
		T
	>
) {
	return useQuery<
		AnchorsQueryResponseModel,
		AxiosError<HTTPValidationError>,
		T
	>({
		queryKey: ['anchor-assortment', params],
		queryFn: () => client.anchors.anchorsQuery(params),
		...(options || {}),
	})
}

export function useAnchorsAlertsAnnotationsQuery<T = AnchorsAlertsItemModel[]>(
	options?: UseQueryOptions<AnchorsAlertsItemModel[], Error, T>
) {
	return useQuery<AnchorsAlertsItemModel[], Error, T>({
		queryKey: ['anchors-alerts'],
		queryFn: () => client.anchors.anchorsAlerts(),
		...(options || {}),
	})
}
export function useAnchorsAlertsQuery<T = AnchorsAlertsInfoModel[]>(
	params: AnchorsAlertsInfoQueryModel,
	options?: UseQueryOptions<AnchorsAlertsInfoModel[], Error, T>
) {
	return useQuery<AnchorsAlertsInfoModel[], Error, T>({
		queryKey: ['anchors-alerts-annotations', params],
		queryFn: () => client.anchors.anchorsAlertsInfoQuery(params),
		...(options || {}),
	})
}
type RowType = AnchorResponseModel & { is_anchor: boolean }

type AnchorRelationGroup = {
	type: string
	rows: RowType[]
}
export function useAnchorsRelationsByTypeQuery<T = AnchorRelationGroup[]>(
	anchor: AnchorResponseModel,
	params: AnchorsQueryModel,
	options?: UseQueryOptions<AnchorRelationGroup[], Error, T>
) {
	return useQuery<AnchorRelationGroup[], Error, T>({
		queryKey: ['anchors-relation-groups', anchor],
		queryFn: async () => {
			const results = await Promise.all(
				ANCHORS_TYPES.map(async (type) => {
					const rows = await client.anchors
						.anchorsQuery({
							...params,
							filters: [
								{
									name: `${type}_anchor_sku`,
									operation: Operators.IS as const,
									value: anchor?.[`${type}_anchor_sku`] || anchor.sku,
								},
								{
									name: 'sales_entity_id',
									operation: Operators.IS as const,
									value: anchor.sales_entity_id!,
								},
								{
									name: 'optimization_group_id',
									operation: Operators.IS as const,
									value: anchor.optimization_group_id!,
								},
							],
						})
						.then((res) => res.data)
					if (isEmpty(rows)) {
						return { type, rows: [] as RowType[] }
					}
					if (anchor?.[`${type}_anchor_sku`]) {
						const parent = await client.anchors
							.anchorsQuery({
								filters: [
									{
										name: 'sku',
										operation: Operators.IS as const,
										value: anchor?.[`${type}_anchor_sku`] || '',
									},
									{
										name: 'sales_entity_id',
										operation: Operators.IS as const,
										value: anchor.sales_entity_id!,
									},
									{
										name: 'optimization_group_id',
										operation: Operators.IS as const,
										value: anchor.optimization_group_id!,
									},
								],
							})
							.then((res) =>
								isEmpty(res.data)
									? //if parent product is missing in response, display SKU only
										{ sku: anchor?.[`${type}_anchor_sku`] }
									: { ...head(res.data) }
							)

						return {
							type,
							rows: [{ ...parent, is_anchor: true }, ...rows] as RowType[],
						}
					}

					return {
						type,
						rows: [{ ...anchor, is_anchor: true }, ...rows] as RowType[],
					}
				})
			)

			return results
		},
		...(options || {}),
	})
}

export function useAnchorsStrategiesQuery<T = AnchorStrategyQueryResponseModel>(
	params: AnchorStrategyQueryModel,
	options?: UseQueryOptions<AnchorStrategyQueryResponseModel, Error, T>
) {
	return useQuery<AnchorStrategyQueryResponseModel, Error, T>({
		queryKey: ['anchors-strategies', params],
		queryFn: () => client.anchorStrategies.anchorsAnchorStrategyQuery(params),
		...(options || {}),
	})
}
export function useGeneratedAnchorFamiliesQuery<
	T = AnchorGeneratedFamilyQueryResponseModel,
>(
	params: AnchorGeneratedFamilyQueryModel,
	options?: UseQueryOptions<AnchorGeneratedFamilyQueryResponseModel, Error, T>
) {
	return useQuery<AnchorGeneratedFamilyQueryResponseModel, Error, T>({
		queryKey: ['generated-anchor-families', params],
		queryFn: () => client.anchorStrategies.anchorsGeneratedFamilyQuery(params),
		...(options || {}),
	})
}
export function useAnchorStrategyMatchedProductsQuery<
	T = Record<string, { products: number; families: number }>,
>(
	params: AnchorStrategyMatchedProductsRequest,
	options?: UseQueryOptions<AnchorStrategyGroupMatchedModel[], Error, T>
) {
	return useQuery<AnchorStrategyGroupMatchedModel[], Error, T>({
		queryKey: ['anchors-strategy-matched-products', params],
		queryFn: () =>
			client.anchorStrategies.anchorsAnchorStrategyMatchedProducts(params),

		...(options || {}),
	})
}
export function useAnchorsSalesEntityQuery<T = AnchorsSalesEntityResponseModel>(
	optimizationGroupId: string,
	params: RequestParams,
	options?: UseQueryOptions<AnchorsSalesEntityResponseModel, Error, T>
) {
	return useQuery<AnchorsSalesEntityResponseModel, Error, T>({
		queryKey: ['anchors-sales-entity', optimizationGroupId],
		queryFn: () =>
			client.anchors.anchorsSalesEntityGet({ optimizationGroupId }, params),
		...(options || {}),
	})
}
export function useAnchorsProductCatalogFiltersQuery<T = FilterRuleModel[][]>(
	data: Record<string, string[]>,
	params: RequestParams,
	options?: UseQueryOptions<FilterRuleModel[][], Error, T>
) {
	return useQuery<FilterRuleModel[][], Error, T>({
		queryKey: ['anchors-product-catalog-filters', data, params],
		queryFn: async () => {
			const promises = Object.entries(data).map(
				async ([optimizationGroupId, skus]) => {
					const response = await client.anchors.anchorsSalesEntityGet(
						{ optimizationGroupId },
						params
					)

					return [
						{
							name: 'sku',
							operation: Operators.IN as const,
							value: skus,
						},
						{
							name: 'sales_entity_id',
							operation: Operators.IS as const,
							value: response.sales_entity_id,
						},
					]
				}
			)

			return await Promise.all(promises)
		},
		...(options || {}),
	})
}

export function useAnchorsProductCatalogQuery<
	T = ProductCatalogQueryResponseModel,
>(
	params: ProductCatalogQueryModel,
	options?: UseQueryOptions<ProductCatalogQueryResponseModel, Error, T>
) {
	return useQuery<ProductCatalogQueryResponseModel, Error, T>({
		queryKey: ['anchors-product-catalog', params],
		queryFn: () => client.productCatalog.anchorsProductCatalogQuery(params),
		...(options || {}),
	})
}
